import {
    type PopupRequest,
    PublicClientApplication,
} from '@azure/msal-browser';
import {
    AZURE_SCOPES,
    getAzureConfig,
} from 'routes/LoginPage/sso/constants/azure';
import {
    isBindUsingOIDC,
    OIDC_CONFIG_FOR_BIND,
} from 'routes/LoginPage/sso/constants/oidc';
import { tempLogSSOStage } from 'routes/LoginPage/sso/helpers';

const getBindSSOConfig = (bind: string) =>
    isBindUsingOIDC(bind) ? OIDC_CONFIG_FOR_BIND[bind] : getAzureConfig(bind);

const getPopupRequest = (bind: string): PopupRequest =>
    isBindUsingOIDC(bind) ? { scopes: ['openid'] } : { scopes: AZURE_SCOPES };

/**
 * Shows a popup to the user to login with the SSO issuer and returns the access token if successful.
 * It uses msal-browser, which is the official client for Azure AD, but it's compatible with standards
 * like OIDC, so we can use it with other SSO issuers.
 *
 * @returns the short lived access token that we exchange for a Manty token in the backend. This token
 * contains the user's identity (email, name, etc) so we can match it with a Manty user.
 */
export const getSSOAccessToken = async (bind: string, attemptId: string) => {
    const startedAt = Date.now();
    const onBridgeMessage = (event: MessageEvent) => {
        const { data }: { data: unknown } = event;
        if (
            event.origin !== window.location.origin ||
            !event.source ||
            event.source === window ||
            typeof data !== 'object' ||
            data === null ||
            !('source' in data) ||
            data.source !== 'manty-sso-bridge' ||
            !('stage' in data) ||
            !['loaded', 'failed'].includes(String(data.stage))
        ) {
            return;
        }

        tempLogSSOStage({
            bind,
            attemptId,
            stage: `bridge_${data.stage}`,
            durationMs: Date.now() - startedAt,
            level: data.stage === 'failed' ? 'warn' : 'info',
        });
    };

    if (bind === 'ampmetropole' || bind === 'cove') {
        window.addEventListener('message', onBridgeMessage);
    }

    try {
        // we instantiate the MSAL client with the configuration for the bind.
        const msal = new PublicClientApplication(getBindSSOConfig(bind));
        await msal.initialize();
        tempLogSSOStage({ bind, attemptId, stage: 'msal_initialized' });
        const request = getPopupRequest(bind);
        tempLogSSOStage({ bind, attemptId, stage: 'popup_started' });
        // this opens a popup showing the issuer login page (Azure, Keycloak...) and return the access token if successful
        const { accessToken } = await msal.loginPopup(request);
        tempLogSSOStage({
            bind,
            attemptId,
            stage: 'popup_completed',
            durationMs: Date.now() - startedAt,
        });
        return accessToken;
    } finally {
        window.removeEventListener('message', onBridgeMessage);
    }
};
